import { useCallback, useRef } from "react";

export interface HtmlAuthoredMotionState {
  html: string;
  revision: number;
}

export function initialHtmlAuthoredMotionState(
  html: string,
  streaming: boolean,
): HtmlAuthoredMotionState {
  return { html, revision: streaming && html ? 1 : 0 };
}

export function advanceHtmlAuthoredMotionState(
  current: HtmlAuthoredMotionState,
  html: string,
  animate: boolean,
): HtmlAuthoredMotionState {
  if (current.html === html) return current;
  return {
    html,
    revision: animate && html ? current.revision + 1 : current.revision,
  };
}

export function useHtmlAuthoredMotionRevision(
  html: string,
  streaming: boolean,
  editing: boolean,
): { revision: number; suppressNextChange: () => void } {
  const stateRef = useRef(initialHtmlAuthoredMotionState(html, streaming));
  const suppressNextRef = useRef(false);
  const changed = stateRef.current.html !== html;
  stateRef.current = advanceHtmlAuthoredMotionState(
    stateRef.current,
    html,
    !editing && !suppressNextRef.current,
  );
  if (changed) suppressNextRef.current = false;
  const suppressNextChange = useCallback(() => {
    suppressNextRef.current = true;
  }, []);
  return { revision: stateRef.current.revision, suppressNextChange };
}

/**
 * Runs inside generated HTML before its authored document is parsed. The
 * iframe element remembers the consumed revision, so theme/edit reloads do
 * not replay an already-read document.
 */
export function htmlAuthoredMotionRuntime(revision: number): string {
  const safeRevision = Math.max(1, Math.round(revision));
  return `<style data-denker-runtime="authored-motion">
html[data-denker-authored-motion="pending"] body > :where(:not(script):not(style):not(link):not(meta)){
  opacity:0;
}
html[data-denker-authored-motion="active"] [data-denker-authored-block="true"]{
  opacity:0;
  filter:blur(3px);
  transform:translateY(7px);
  animation:denker-authored-block 520ms cubic-bezier(.2,.8,.2,1) both;
  animation-delay:calc(var(--denker-authored-order,0) * 92ms);
}
html[data-denker-authored-motion="active"] [data-denker-authored-draw="true"]{
  stroke-dasharray:var(--denker-authored-length);
  stroke-dashoffset:var(--denker-authored-length);
  animation:denker-authored-draw 760ms cubic-bezier(.4,0,.2,1) both;
  animation-delay:calc(180ms + var(--denker-authored-order,0) * 54ms);
}
@keyframes denker-authored-block{
  to{opacity:1;filter:blur(0);transform:translateY(0)}
}
@keyframes denker-authored-draw{
  to{stroke-dashoffset:0}
}
@media(prefers-reduced-motion:reduce){
  html[data-denker-authored-motion] body > *,
  html[data-denker-authored-motion] [data-denker-authored-block="true"],
  html[data-denker-authored-motion] [data-denker-authored-draw="true"]{
    opacity:1;filter:none;transform:none;animation:none;stroke-dashoffset:0
  }
}
</style>
<script data-denker-runtime="authored-motion">
(function(){
  var revision="${safeRevision}";
  var frame=window.frameElement;
  if(frame&&frame.getAttribute("data-authored-motion-consumed")===revision)return;
  if(frame)frame.setAttribute("data-authored-motion-consumed",revision);
  if(window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches)return;
  var root=document.documentElement;
  root.setAttribute("data-denker-authored-motion","pending");
  function setup(){
    if(!document.body)return;
    var direct=Array.prototype.filter.call(document.body.children,function(el){
      return ["SCRIPT","STYLE","LINK","META"].indexOf(el.tagName)===-1;
    });
    var scope=direct.length===1&&direct[0].children.length>1?direct[0]:document.body;
    var blocks=Array.prototype.filter.call(scope.children,function(el){
      return ["SCRIPT","STYLE","LINK","META"].indexOf(el.tagName)===-1;
    }).slice(0,18);
    blocks.forEach(function(el,index){
      el.setAttribute("data-denker-authored-block","true");
      el.style.setProperty("--denker-authored-order",String(index));
    });
    var shapes=Array.prototype.slice.call(document.querySelectorAll("svg path,svg line,svg polyline,svg polygon,svg circle,svg ellipse,svg rect"),0,48);
    shapes.forEach(function(el,index){
      if(typeof el.getTotalLength!=="function")return;
      try{
        var length=Math.max(1,Math.ceil(el.getTotalLength()));
        el.setAttribute("data-denker-authored-draw","true");
        el.style.setProperty("--denker-authored-length",String(length));
        el.style.setProperty("--denker-authored-order",String(index));
      }catch(error){}
    });
    requestAnimationFrame(function(){
      root.setAttribute("data-denker-authored-motion","active");
    });
    var blockEnd=520+Math.max(0,blocks.length-1)*92;
    var shapeEnd=940+Math.max(0,shapes.length-1)*54;
    setTimeout(function(){
      root.removeAttribute("data-denker-authored-motion");
      blocks.forEach(function(el){
        el.removeAttribute("data-denker-authored-block");
        el.style.removeProperty("--denker-authored-order");
      });
      shapes.forEach(function(el){
        el.removeAttribute("data-denker-authored-draw");
        el.style.removeProperty("--denker-authored-length");
        el.style.removeProperty("--denker-authored-order");
      });
    },Math.max(blockEnd,shapeEnd)+120);
  }
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",setup,{once:true});
  else setup();
})();
</scr${""}ipt>`;
}
